import React, { useEffect, useRef, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { FiAlertTriangle } from 'react-icons/fi';
import { Tooltip } from "flowbite-react";
import { DashboardStatus, getDashboardStatusColor } from "../../../../constants/integrationTypes";
import { AnimatedNumber as AnimatedNumberShared, AnimatedWidthSlot } from "../../../../components/common/Animated";
import BestFitPeriodSelector from '../../../../pages/DashboardNew/BestFitPeriodSelector';
import * as def from "../../StrategyMapView.def";
import "./StrategyMapActionHeader.css";
import { formatDateRange } from '../../../../utils/timeUtils';
import BestFitPeriodSelectorStrategyMap from './StrategyMapTimeLineSelection';
import { StrategyMapActionSkeleton } from './StrategyMapActionSkeleton';



const ICON_SLOT_PX = 18;

/**
 * StrategyMapActionHeader - Replicates the TimelineNavBar UI for Strategy Map View.
 * Managed by parent state for view mode and time periods.
 */
export function StrategyMapActionHeader({ state }: {state: def.StrategyMapViewStateProps}) {
    const { viewMode, timelineViewType, stats } = state.get;

    if (!state?.get?.strageyMapData || state?.get?.loadingSingleStrategyMap) return <StrategyMapActionSkeleton/>
    
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
    const [showAllStatuses, setShowAllStatuses] = useState(false);
    
    const timelineButtonRef = useRef<HTMLButtonElement>(null);
    const tableButtonRef = useRef<HTMLButtonElement>(null);

    const updateIndicator = () => {
        const activeButton = viewMode === 'timeline' ? timelineButtonRef.current : tableButtonRef.current;
        if (activeButton) {
            setIndicatorStyle({
                width: activeButton.offsetWidth,
                left: activeButton.offsetLeft
            });
        }
    };

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            updateIndicator();
        });
        return () => cancelAnimationFrame(frame);
    }, [viewMode]);

    useEffect(() => {
        window.addEventListener('resize', updateIndicator);
        return () => window.removeEventListener('resize', updateIndicator);
    }, [viewMode]);

    const handleViewChange = (mode: 'timeline' | 'list') => {
        state.set((prev: def.StrategyMapViewStateType) => ({ ...prev, viewMode: mode }));
    };

    const handlePeriodChange = (period: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly', isBestMode?: boolean) => {
        state.set(
          (prev: def.StrategyMapViewStateType) => (
            { 
              ...prev,
              timelineViewType: period,
              timeLineViewAutoMode: isBestMode ?? prev.timeLineViewAutoMode 
            }
          )
        );
    };

    return (
      <div
        className={`sma-header ${state.get.simulation && "bg-light-headerSimulation border-gray-300 dark:border-gray-700 dark:bg-[#00a86b1c]"}`}
      >
        {/* Left side - View switcher */}
        <div className="flex items-center">
          <div className="sma-switcher-container">
            {/* Animated indicator */}
            <div
              className="sma-switcher-indicator"
              style={{
                width: indicatorStyle.width,
                left: indicatorStyle.left,
              }}
            />

            <button
              ref={timelineButtonRef}
              onClick={() => handleViewChange("timeline")}
              className={`sma-switcher-btn ${viewMode === "timeline" ? "sma-switcher-btn-active" : "sma-switcher-btn-inactive"}`}
            >
              Timeline view
            </button>

            <button
              ref={tableButtonRef}
              onClick={() => handleViewChange("list")}
              className={`sma-switcher-btn ${viewMode === "list" ? "sma-switcher-btn-active" : "sma-switcher-btn-inactive"}`}
            >
              List view
            </button>
          </div>

          {viewMode === "timeline" && state.get.strageyMapData && (
            <BestFitPeriodSelectorStrategyMap
              period={timelineViewType as any}
              data={state.get.strageyMapData}
              autoMode={state.get.timeLineViewAutoMode}
              onChange={handlePeriodChange}
            />
          )}
        </div>

        {/* Right side - Statistics and Date Range */}
        <div className="sma-stats-wrapper">
          <div className="sma-stats-group">
            {/* Delayed Stat */}
            <div className="sma-stat-item">
              <span className="sma-stat-label">
                <AnimatedWidthSlot
                  show={Number(stats.at_risk) > 0}
                  width={ICON_SLOT_PX}
                >
                  <Tooltip content="Contributes to Risk" placement="top">
                    <FiAlertTriangle className="text-red-500" />
                  </Tooltip>
                </AnimatedWidthSlot>
                At Risk
              </span>
              <div className="sma-stat-value-container">
                <div
                  className={`sma-stat-dot ${getDashboardStatusColor(DashboardStatus.AT_RISK)}`}
                />
                <AnimatedNumberShared
                  className="sma-stat-value"
                  value={Number(stats.at_risk) || 0}
                />
              </div>
            </div>

            <div className="sma-stat-item">
              <span className="sma-stat-label">
                <AnimatedWidthSlot
                  show={Number(stats.on_track) > 0}
                  width={ICON_SLOT_PX}
                >
                  <Tooltip content="Contributes to Risk" placement="top">
                    <FiAlertTriangle className="text-red-500" />
                  </Tooltip>
                </AnimatedWidthSlot>
                On Track
              </span>
              <div className="sma-stat-value-container">
                <div
                  className={`sma-stat-dot ${getDashboardStatusColor(DashboardStatus.ON_TRACK)}`}
                />
                <AnimatedNumberShared
                  className="sma-stat-value"
                  value={Number(stats.on_track) || 0}
                />
              </div>
            </div>

            {/* Blocked Stat */}
            <div className="sma-stat-item">
              <span className="sma-stat-label">Completed</span>
              <div className="sma-stat-value-container">
                <div
                  className={`sma-stat-dot ${getDashboardStatusColor(DashboardStatus.COMPLETED)}`}
                />
                <AnimatedNumberShared
                  className="sma-stat-value"
                  value={Number(stats.completed) || 0}
                />
              </div>
            </div>
          </div>

          {/* Others expandable section */}
          {!showAllStatuses && (
            <div className="relative">
              <button
                onClick={() => setShowAllStatuses(!showAllStatuses)}
                className="flex flex-col items-start gap-1 hover:opacity-80 transition-opacity duration-200"
              >
                <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                  Others
                  <HiChevronDown className="w-3 h-3 -rotate-90" />
                </span>
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      background:
                        "conic-gradient(#3b82f6 0% 25%, #8b5cf6 25% 50%, #22c55e 50% 75%, #6b7280 75% 100%)",
                    }}
                  ></div>
                  <AnimatedNumberShared
                    className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                    value={
                     
                      (Number(stats.not_started) || 0) +
                      (Number(stats.closed) || 0)
                    }
                  />
                </div>
              </button>
            </div>
          )}

          {showAllStatuses && (
            <>
              <div className="sma-stat-item">
                <span className="sma-stat-label">Closed</span>
                <div className="sma-stat-value-container">
                  <div
                    className={`sma-stat-dot ${getDashboardStatusColor(DashboardStatus.CLOSED)}`}
                  />
                  <AnimatedNumberShared
                    className="sma-stat-value"
                    value={Number(stats.closed) || 0}
                  />
                </div>
              </div>
              <div className="sma-stat-item">
                <span className="sma-stat-label">Not Started</span>
                <div className="sma-stat-value-container">
                  <div
                    className={`sma-stat-dot ${getDashboardStatusColor(DashboardStatus.NOT_STARTED)}`}
                  />
                  <AnimatedNumberShared
                    className="sma-stat-value"
                    value={Number(stats.not_started) || 0}
                  />
                </div>
              </div>
              <div className="flex items-center justify-center">
                                      <button
                                        onClick={() => setShowAllStatuses(false)}
                                        className="text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-gray-300 transition-colors duration-200 p-1"
                                      >
                                        <HiChevronDown className="w-4 h-4 rotate-90" />
                                      </button>
                                    </div>
            </>
          )}

          {/* Date Section */}
          <div className="sma-date-section">
            <span className="sma-date-label">Start and end date</span>
            <span className="sma-date-value">
              {formatDateRange(state.get.strageyMapData?.start_date)} -{" "}
              {formatDateRange(state.get.strageyMapData?.end_date)}
            </span>
          </div>
        </div>
      </div>
    );
}

export default StrategyMapActionHeader;